import React, { useState, useRef, useImperativeHandle, forwardRef, ForwardedRef } from 'react';

import classNames from 'classnames';
import ReactModal from 'react-modal';

import {
  InterstellarAccount,
  previewDeleteFreeswitchStack,
  PreviewDeleteFreeswitchResponse,
} from '../api';

export interface DeleteAccountModalRef {
  show: (account: InterstellarAccount) => void;
  hide: () => void;
}

interface DeleteAccountModalProps {
  onConfirm: (account: InterstellarAccount, deleteInfra: boolean) => void;
  isDeleting?: boolean;
}

type ModalStep = 'confirm-name' | 'confirm-infra';

export const DeleteAccountModal = forwardRef(
  (
    { onConfirm, isDeleting = false }: DeleteAccountModalProps,
    ref: ForwardedRef<DeleteAccountModalRef>
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [account, setAccount] = useState<InterstellarAccount | null>(null);
    const [confirmText, setConfirmText] = useState('');
    const [deleteInfra, setDeleteInfra] = useState(false);
    const [step, setStep] = useState<ModalStep>('confirm-name');
    const [infraPreview, setInfraPreview] = useState<PreviewDeleteFreeswitchResponse | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const resetState = (): void => {
      setIsOpen(false);
      setAccount(null);
      setConfirmText('');
      setDeleteInfra(false);
      setStep('confirm-name');
      setInfraPreview(null);
      setLoadingPreview(false);
      setPreviewError(null);
    };

    useImperativeHandle(ref, () => ({
      show: (acc: InterstellarAccount) => {
        setAccount(acc);
        setConfirmText('');
        setDeleteInfra(false);
        setStep('confirm-name');
        setInfraPreview(null);
        setPreviewError(null);
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      },
      hide: resetState,
    }));

    const closeModal = (): void => {
      if (isDeleting || loadingPreview) return;
      resetState();
    };

    const handleProceed = async (): Promise<void> => {
      if (!account || confirmText !== account.name) return;

      if (deleteInfra) {
        // Fetch preview before showing confirmation
        setLoadingPreview(true);
        setPreviewError(null);
        try {
          const preview = await previewDeleteFreeswitchStack(account.id);
          setInfraPreview(preview);
          setStep('confirm-infra');
        } catch (err) {
          const axiosErr = err as { response?: { data?: { detail?: string } } };
          setPreviewError(
            axiosErr?.response?.data?.detail ||
              'No se pudo obtener informacion de la infraestructura'
          );
        } finally {
          setLoadingPreview(false);
        }
      } else {
        // No infra deletion, proceed directly
        onConfirm(account, false);
      }
    };

    const handleConfirmDelete = (): void => {
      if (!account) return;
      onConfirm(account, true);
    };

    const handleBack = (): void => {
      setStep('confirm-name');
      setInfraPreview(null);
      setPreviewError(null);
    };

    const canProceed = account && confirmText === account.name && !isDeleting && !loadingPreview;

    return (
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeModal}
        className="modal modal-sm"
        overlayClassName="overlay"
        appElement={document.querySelector('#root') as HTMLElement}
      >
        {step === 'confirm-name' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-black-1.0">Eliminar cuenta</h2>
              <p className="mt-1 text-sm text-gray-2.0">
                Esta accion eliminara la cuenta <strong>{account?.name}</strong> y todos sus
                recursos asociados (usuarios, modulos, audios, horarios) de Interstellar.
              </p>
            </div>

            {!account?.freeswitch_group_id && (
              <label className="hover:bg-gray-50 flex items-start gap-3 p-3 transition-colors border rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteInfra}
                  onChange={(e) => setDeleteInfra(e.target.checked)}
                  disabled={isDeleting || loadingPreview}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-black-1.0">
                    Tambien eliminar infraestructura de AWS
                  </span>
                  <p className="mt-0.5 text-xs text-gray-2.0">
                    Elimina el servidor EC2, la base de datos y los archivos de configuracion.
                  </p>
                </div>
              </label>
            )}

            {account?.freeswitch_group_id && (
              <div className="bg-blue-50 p-3 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Cuenta compartida:</strong> Esta cuenta usa un FreeSWITCH compartido. Solo
                  se eliminará la configuración de la cuenta, no la infraestructura.
                </p>
              </div>
            )}

            {!deleteInfra && !account?.freeswitch_group_id && (
              <div className="bg-amber-50 border-amber-200 p-3 border rounded-lg">
                <p className="text-amber-800 text-xs">
                  <strong>Nota:</strong> Si no marcas la opcion anterior, la infraestructura de AWS
                  seguira activa y deberas eliminarla manualmente.
                </p>
              </div>
            )}

            {previewError && (
              <div className="bg-red-50 p-3 border border-red-200 rounded-lg">
                <p className="text-xs text-red-800">{previewError}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-black-1.0">
                Escribe <strong>{account?.name}</strong> para confirmar:
              </label>
              <input
                ref={inputRef}
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={account?.name}
                disabled={isDeleting || loadingPreview}
                className={classNames(
                  'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500',
                  confirmText === account?.name
                    ? 'border-green-500 bg-green-50'
                    : 'border-white-2.0 bg-white-0.0'
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canProceed) {
                    handleProceed();
                  }
                }}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeModal}
                disabled={isDeleting || loadingPreview}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleProceed}
                disabled={!canProceed}
                className={classNames(
                  'btn flex items-center gap-2',
                  canProceed ? 'btn-danger' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                {loadingPreview ? (
                  <>
                    <span className="animate-spin border-t-transparent w-4 h-4 border-2 border-white rounded-full" />
                    Verificando...
                  </>
                ) : isDeleting ? (
                  <>
                    <span className="animate-spin border-t-transparent w-4 h-4 border-2 border-white rounded-full" />
                    Eliminando...
                  </>
                ) : deleteInfra ? (
                  'Continuar'
                ) : (
                  'Eliminar cuenta'
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'confirm-infra' && infraPreview && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-red-600">Confirmar eliminacion de AWS</h2>
              <p className="mt-1 text-sm text-gray-2.0">
                Verifica que esta es la infraestructura correcta antes de eliminar:
              </p>
            </div>

            <div className="bg-gray-50 p-4 space-y-2 font-mono text-sm border rounded-lg">
              <div className="flex justify-between">
                <span className="text-gray-500">Instancia EC2:</span>
                <span className="font-semibold text-black-1.0">
                  {infraPreview.instance_id || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">IP Privada:</span>
                <span className="text-black-1.0">{infraPreview.instance_private_ip || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Stack:</span>
                <span className="text-black-1.0">{infraPreview.stack_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cliente:</span>
                <span className="text-black-1.0">{infraPreview.client_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cosmos UUID:</span>
                <span className="text-black-1.0 text-xs">{infraPreview.cosmos_account_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Estado:</span>
                <span className="text-black-1.0">{infraPreview.stack_status}</span>
              </div>
              {infraPreview.creation_time && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Creado:</span>
                  <span className="text-black-1.0">
                    {new Date(infraPreview.creation_time).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-red-50 p-3 border border-red-300 rounded-lg">
              <p className="text-sm text-red-800">{infraPreview.warning}</p>
            </div>

            <a
              href={infraPreview.console_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline block text-xs text-blue-600"
            >
              Ver en AWS Console →
            </a>

            <div className="flex justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={handleBack}
                disabled={isDeleting}
                className="btn btn-ghost"
              >
                ← Atras
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isDeleting}
                  className="btn btn-ghost"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="btn btn-danger flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <span className="animate-spin border-t-transparent w-4 h-4 border-2 border-white rounded-full" />
                      Eliminando...
                    </>
                  ) : (
                    'Eliminar definitivamente'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </ReactModal>
    );
  }
);
